import React from 'react'
import classNames from 'classnames'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { PlayCircleIcon } from '@/atoms/Icons/PlayCircleIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { CaptionSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { LivestreamThumbnailPill } from '@/molecules/LivestreamThumbnailPill'
import { LivestreamTitle } from '@/page/LivestreamLandscapeRowCollection/LivestreamTitle'
import { useTranslate } from '@/utils/translate/translate-client'

interface CurrentLivestreamThumbnailProps {
  live?: boolean
  guid: string
  bannerCloudinaryPath: string
  startAt: string
  isEnded: boolean
  className?: string
  animationClassName?: string
  aspectRatioClassName?: string
  name?: string
  unavailableReason?: string | null
}

export const LivestreamThumbnail: React.FC<CurrentLivestreamThumbnailProps> = ({
  live = false,
  guid,
  bannerCloudinaryPath,
  startAt,
  isEnded,
  className,
  aspectRatioClassName = 'aspect-w-16 aspect-h-9',
  animationClassName = 'duration-[400ms] ease-in-out',
  name,
  unavailableReason,
}) => {
  const { t } = useTranslate('watch')
  if (typeof guid === 'undefined' || guid === null || !bannerCloudinaryPath) return null
  const href = `${paths.livestream.index}/${guid}`

  return (
    <div className="flex flex-row items-start gap-4">
      <InternalLink href={href}>
        <div
          className={classNames(
            `relative rounded-lg overflow-hidden cursor-pointer flex-none w-[146px] lg:w-[316px]`,
            { 'border-red-400 border-4': live },
            aspectRatioClassName,
            animationClassName,
            className,
          )}
        >
          <div className="-z-5 absolute h-full w-full">
            <Image
              alt={`${name} Livestream Thumbnail`}
              src={bannerCloudinaryPath}
              className="h-full max-h-[82px] w-full max-w-[146px] lg:max-h-[177px] lg:max-w-[316px]"
              width={316}
              height={177}
            />
          </div>
          <div className="absolute inset-0">
            <div
              className={classNames(
                'w-full h-full flex justify-center items-center opacity-0 transition',
                animationClassName,
              )}
            >
              <div className="mx-auto h-fit w-fit !opacity-100">
                <PlayCircleIcon />
              </div>
            </div>
            <div className="absolute bottom-2 right-2">
              <LivestreamThumbnailPill
                live={Boolean(live)}
                isEnded={isEnded}
                startAt={startAt}
                isGuildOnly={unavailableReason === 'PRERELEASE'}
              />
            </div>
          </div>
        </div>
      </InternalLink>
      <div className="space-between flex h-full w-full flex-row items-center gap-x-4 lg:gap-x-0">
        <div className="flex flex-col gap-[6px]">
          <CaptionSM color="core-gray-500" className="md:photon-caption-md lg:photon-caption-lg">
            {t('livestream', 'Livestream')}
          </CaptionSM>
          <CaptionSM weight="bold" className="lg:photon-title-md line-clamp-2 lg:line-clamp-4">
            {name}
          </CaptionSM>
          <LivestreamTitle
            live={live}
            isEnded={isEnded}
            startAt={startAt}
            className="!photon-caption-sm lg:!photon-caption-lg !text-core-gray-500"
          />
        </div>
        <a className="lg:hidden" href={href}>
          <CaretRightIcon size={24} color="white" />
        </a>
      </div>
    </div>
  )
}
